import { Stack, Typography, TypographyProps } from "@mui/material";
import { isEmpty } from "lodash-es";
import { ReactNode } from "react";

import SimpleViewPageHeader from "@/App/Layouts/PageHeader/SimpleViewPageHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import VersionDisplay from "@/common/components/Display/VersionDisplay";
import { EntityChipList } from "@/common/components/EntityInfo/EntityChipList";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import InlineApiEnumValueList from "@/common/components/Enum/InlineApiEnumValueList";
import FieldValue from "@/common/components/Form/Display/FieldValue";
import AppLink from "@/common/components/Link/AppLink";
import { AppModalCloseIconButton } from "@/common/components/Modals/AppModalCloseIconButton";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { EntityViewVariant, ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  DataUpdatesHubClientMethodName,
  EntityType,
  VehicleVisualModelDto,
} from "@/core/api/generated/v1";

import VehicleGenerationInline from "../../ReferenceData/VehicleGeneration/VehicleGenerationInline";
import VehicleMakeInline from "../../ReferenceData/VehicleMake/VehicleMakeInline";
import VehicleModelInline from "../../ReferenceData/VehicleModel/VehicleModelInline";
import VehicleModificationInline from "../../ReferenceData/VehicleModification/VehicleModificationInline";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import { VehicleVisualModelImagesDisplay } from "../VehicleVisualModelImagesDisplay";

export enum VehicleVisualModelViewPageTabs {
  Overview = "Overview",
  Pools = "Pools",
}

const defaultHeaderProps = {
  withLink: false,
  typographyProps: undefined as Partial<TypographyProps> | undefined,
  title: undefined as ReactNode | undefined,
};

const defaultDisplayProps = {
  viewVariant: ViewVariant.Page,
  breadcrumbs: true,
  header: true,
  actions: true,
  /** Mainly for modal view.  */
  closeInHeader: true,
};

interface OwnProps extends BaseEntityViewInheritableProps<VehicleVisualModelDto> {
  vehicleVisualModelId: string | null | undefined;
  vehicleVisualModel: VehicleVisualModelDto | null | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  headerProps?: Partial<typeof defaultHeaderProps>;
  /** Mainly for modal view.  */
  onClose?: () => void;
}

export type VehicleVisualModelViewProps = OwnProps;

export default function VehicleVisualModelView({
  vehicleVisualModelId,
  vehicleVisualModel,
  displayProps = defaultDisplayProps,
  headerProps = defaultHeaderProps,
  onClose,
}: VehicleVisualModelViewProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };
  headerProps = {
    ...defaultHeaderProps,
    ...headerProps,
  };

  const currentTenant = useCurrentTenant();

  const visualModelRequest = useApiRequest(
    apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsModelIdGet,
    {
      modelId: vehicleVisualModelId!,
    },
    {
      deps: [vehicleVisualModelId],
      skip: !vehicleVisualModelId || !!vehicleVisualModel,
    },
  );
  vehicleVisualModel = vehicleVisualModel || visualModelRequest?.data;

  useRealtimeDataUpdates({
    channelNames: [
      DataUpdatesChannelName.Entity(
        currentTenant?.id,
        EntityType.VehicleVisualModel,
        vehicleVisualModel?.id || "",
      ),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      visualModelRequest.handleEntityChanged(data);
    },
  });

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb:
      (displayProps?.breadcrumbs &&
        vehicleVisualModel && {
          idValue: vehicleVisualModel.id!,
          newTitle: vehicleVisualModel.name || "",
        }) ||
      undefined,
  });

  return (
    <BaseEntityView
      entityType={EntityType.VehicleVisualModel}
      entityId={vehicleVisualModelId}
      entity={vehicleVisualModel}
      entityRequest={visualModelRequest}
    >
      <ViewLayout
        displayProps={displayProps}
        header={
          <>
            {displayProps.header && (
              <SimpleViewPageHeader
                title={
                  <Typography
                    component='div'
                    variant='h1'
                    sx={{ display: "flex", alignItems: "center" }}
                    {...headerProps?.typographyProps}
                  >
                    {headerProps?.title || (
                      <>
                        <span>Vehicle visual model </span>&nbsp;
                        <AppLink
                          isEnabled={headerProps?.withLink || false}
                          to={ROUTE_PATH.VEHICLE_VISUAL_MODEL_VIEW({
                            vehicleVisualModelId: vehicleVisualModel?.id || undefined,
                          })}
                        >
                          <span>
                            {vehicleVisualModel?.name || vehicleVisualModel?.globalNumber}
                          </span>
                        </AppLink>
                      </>
                    )}{" "}
                    <EntityChipList
                      entity={vehicleVisualModel}
                      entityViewVariant={EntityViewVariant.View}
                      variant='normal'
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                }
                closeAction={
                  displayProps.closeInHeader && onClose ? (
                    <AppModalCloseIconButton onClick={onClose} />
                  ) : undefined
                }
              />
            )}
          </>
        }
      >
        <ViewContentBlock>
          <FieldValue label='Number'>{vehicleVisualModel?.globalNumber}</FieldValue>

          <FieldValue label='Name'>{vehicleVisualModel?.name}</FieldValue>

          <FieldValue label='Version'>
            <VersionDisplay value={vehicleVisualModel?.version} />
          </FieldValue>

          <FieldValue label='Type'>
            <InlineApiEnumValue type='VehicleVisualModelType' value={vehicleVisualModel?.type} />
          </FieldValue>

          <FieldValue label='Areas' isEmpty={isEmpty(vehicleVisualModel?.areas)}>
            <InlineApiEnumValueList type='VehicleArea' values={vehicleVisualModel?.areas} />
          </FieldValue>

          <FieldValue label='Projections' isEmpty={isEmpty(vehicleVisualModel?.projections)}>
            <InlineApiEnumValueList
              type='VehicleProjection'
              values={vehicleVisualModel?.projections}
            />
          </FieldValue>

          <FieldValue label='Vehicle types' isEmpty={isEmpty(vehicleVisualModel?.vehicleTypes)}>
            <InlineApiEnumValueList type='VehicleType' values={vehicleVisualModel?.vehicleTypes} />
          </FieldValue>

          <FieldValue label='Body types' isEmpty={isEmpty(vehicleVisualModel?.bodyTypes)}>
            <InlineApiEnumValueList type='VehicleBodyType' values={vehicleVisualModel?.bodyTypes} />
          </FieldValue>

          <FieldValue label='Makes' isEmpty={isEmpty(vehicleVisualModel?.makes)}>
            <Stack spacing={1}>
              {vehicleVisualModel?.makes?.map((x, i) => (
                <VehicleMakeInline key={i} make={x} />
              ))}
            </Stack>
          </FieldValue>

          <FieldValue label='Models' isEmpty={isEmpty(vehicleVisualModel?.models)}>
            <Stack spacing={1}>
              {vehicleVisualModel?.models?.map((x, i) => (
                <VehicleModelInline key={i} model={x} />
              ))}
            </Stack>
          </FieldValue>

          <FieldValue label='Generations' isEmpty={isEmpty(vehicleVisualModel?.generations)}>
            <Stack spacing={1}>
              {vehicleVisualModel?.generations?.map((x, i) => (
                <VehicleGenerationInline key={i} generation={x} />
              ))}
            </Stack>
          </FieldValue>

          <FieldValue label='Modifications' isEmpty={isEmpty(vehicleVisualModel?.modifications)}>
            <Stack spacing={1}>
              {vehicleVisualModel?.modifications?.map((x, i) => (
                <VehicleModificationInline key={i} modification={x} />
              ))}
            </Stack>
          </FieldValue>
        </ViewContentBlock>

        <ViewContentBlock>
          <Typography variant='h4'>Images</Typography>

          {/* Old */}
          {/* {vehicleVisualModel && (
            <VisualModelImagesList
              visualModel={vehicleVisualModel}
              imageContainerSx={{
                height: `200px`,
                width: `200px`,
              }}
            />
          )} */}

          {/* New */}
          {vehicleVisualModel && (
            <VehicleVisualModelImagesDisplay
              visualModel={vehicleVisualModel}
              imageProps={{
                width: 200,
                height: 200,
              }}
            />
          )}
        </ViewContentBlock>
      </ViewLayout>
    </BaseEntityView>
  );
}
